// components/common/LanguageSwitcher.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FlagIcon from 'react-flagkit';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  fontSize: '14px',
  fontWeight: 500,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    padding: theme.spacing(1.2, 1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const LanguageOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

function LanguageSwitcher() {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const languages = [
    {
      code: 'da',
      name: t('common:language.danish'),
      countryCode: 'DK',
    },
    {
      code: 'nb',
      name: t('common:language.norwegian'),
      countryCode: 'NO',
    },
    {
      code: 'sv',
      name: t('common:language.swedish'),
      countryCode: 'SE',
    },
    {
      code: 'en',
      name: t('common:language.english'),
      countryCode: 'GB',
    },
    {
      code: 'de',
      name: t('common:language.german'),
      countryCode: 'DE',
    },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <StyledFormControl size="small">
      <StyledSelect
        value={i18n.language}
        onChange={handleLanguageChange}
        displayEmpty
        renderValue={(value) => {
          const lang = languages.find(l => l.code === value);
          return (
            <LanguageOption>
              {lang?.countryCode && <FlagIcon country={lang.countryCode} size={20} />}
              <Box>
                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {lang?.name || t('common:language.select_language')}
                </Typography>
              </Box>
            </LanguageOption>
          );
        }}
      >
        {languages.map((language) => (
          <MenuItem key={language.code} value={language.code}>
            <LanguageOption>
              <FlagIcon country={language.countryCode} size={18} />
              <Typography variant="body2" sx={{ fontSize: '14px' }}>
                {language.name}
              </Typography>
            </LanguageOption>
          </MenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
}

export default LanguageSwitcher;